import { useEffect } from "react";
import { createPortal } from "react-dom";

interface Props {
  x: number;
  y: number;
  canCopy: boolean;
  onCopy: () => void;
  onPaste: () => void;
  onClose: () => void;
}

export function TerminalContextMenu({ x, y, canCopy, onCopy, onPaste, onClose }: Props) {
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Defer the click listener one tick so the opening contextmenu event
    // doesn't immediately close the menu.
    const id = window.setTimeout(() => window.addEventListener("click", close), 0);
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Portal to <body> so position:fixed resolves against the viewport. In the
  // Canvas layout the terminal nests under .terminal-pane__canvas-inner, which
  // is transform:scale()-ed — a transformed ancestor becomes the containing
  // block for position:fixed, so an in-tree menu would be offset from the
  // cursor (the click coords are viewport coords). The drag ghost escapes the
  // same way (document.body.appendChild). (Codex P2.)
  return createPortal(
    <ul
      className="terminal-context-menu"
      style={{ position: "fixed", left: x, top: y, zIndex: 1000 }}
      role="menu"
      onClick={(e) => e.stopPropagation()}
    >
      <li role="none">
        <button
          type="button"
          role="menuitem"
          disabled={!canCopy}
          onClick={() => {
            onCopy();
            onClose();
          }}
        >
          Copy
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onPaste();
            onClose();
          }}
        >
          Paste
        </button>
      </li>
    </ul>,
    document.body
  );
}
